import { ApplicationRef, inject, Injectable, signal } from '@angular/core';
import { Hooks, register } from 'register-service-worker';
import { CookieService, LocalstorageService, PlatformService } from 'ngx-unificator/services';
import { GlobalEventsService } from './global-events.service';
import { WA_WINDOW } from 'ngx-unificator/tokens';
import { GoogleTagManagerService } from './google-tag-manager.service';
import { fromEventPattern, merge, of, ReplaySubject } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { NATIVE_APP_COOKIE } from './install-app.service';

@Injectable({
  providedIn: 'root',
})
export class ServiceWorkerService {
  private _platform = inject(PlatformService);
  private _events = inject(GlobalEventsService);
  private _window = inject<Window>(WA_WINDOW);
  private _gtm = inject(GoogleTagManagerService);
  private _applicationRef = inject(ApplicationRef);
  private _cookie = inject(CookieService);
  private _localStorage = inject(LocalstorageService);
  private _deferredPrompt = null;
  private _isEnabled = null;

  public isPwaMode = signal<boolean>(false);
  public appInstalled$: ReplaySubject<boolean> = new ReplaySubject(1);

  get pwaEnabled() {
    return this._isEnabled;
  }

  public initPWA() {
    this._registerSW();
    this._beforeInstallPrompt();
    this._detectPwaMode();
    this._listenAppInstalled();
  }

  private _registerSW(): void {
    if (!this._platform.isBrowser) {
      return;
    }

    this._applicationRef.isStable.pipe(filter(Boolean), first()).subscribe(() => {
      this._window.setTimeout(() => this._doRegisterSW(), 3_500);
    });
  }

  private _doRegisterSW(): void {
    const serviceWorkerPath = `/sw.js`;
    const options: Hooks = {
      registrationOptions: { scope: './' },
      ready: (registration) => {
        console.log('Service worker is active.');
      },
      registered: (registration) => {
      },
      cached: (registration) => {
      },
      updatefound: (registration) => {
      },
      updated: (registration) => {
      },
      offline: () => {
        console.error('No internet connection found. App is running in offline mode.');
      },
      error: (error) => {
        console.error('Error during service worker registration:', error);
      },
    };
    register(serviceWorkerPath, options);
  }

  private _beforeInstallPrompt() {
    if (this._platform.isBrowser) {
      this._events.beforeinstallprompt$.pipe(
        first()
      ).subscribe((e: any) => {
        e.preventDefault();
        this._deferredPrompt = e;
        this._isEnabled = true;
      });
    }
  }

  /**
   * Install PWA app
   */
  public addAppToHomeScreen() {
    if (!this._deferredPrompt) {
      return;
    }

    this._deferredPrompt.prompt();

    const promptInstance = this._deferredPrompt;
    this._deferredPrompt = null;

    promptInstance.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the A2HS prompt');
        this._isEnabled = false;
      } else {
        console.log('User dismissed the A2HS prompt');
        this._isEnabled = true;
        this._deferredPrompt = promptInstance;
      }

      this._gtm.pwaInstallChoice(choiceResult.outcome);
    });
  }

  private _isPwaMode(): boolean {
    return this._platform.isBrowser ? this._window.matchMedia('(display-mode: standalone)')?.matches ||
      (this._window.navigator as any)?.standalone === true : true;
  }

  private _detectPwaMode() {
    if (!this._platform.isBrowser) {
      return;
    }

    const mediaQuery = this._window.matchMedia('(display-mode: standalone)');

    merge(
      of(null),
      fromEventPattern<MediaQueryListEvent>(
        (handler) => mediaQuery.addEventListener('change', handler),
        (handler) => mediaQuery.removeEventListener('change', handler),
      ),
    )
      .pipe(
        map(() => this._isPwaMode()),
        filter(Boolean),
      )
      .subscribe(() => this._markAppAsInstalled());
  }

  private _listenAppInstalled() {
    if (!this._platform.isBrowser) {
      return;
    }

    this._events.appinstalled$.subscribe(() => {
      this._deferredPrompt = null;
      this._isEnabled = false;
      this._markAppAsInstalled();
    });
  }

  private _markAppAsInstalled() {
    this.isPwaMode.set(true);
    this._cookie.set(NATIVE_APP_COOKIE, 'true', 999, '/', this._rootDomain());
    this._localStorage.set(NATIVE_APP_COOKIE, 'true');
    this.appInstalled$.next(true);
  }

  private _rootDomain(): string {
    return this._window.location.hostname.split('.').slice(-2).join('.');
  }
}
